import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../../i18n/translations';
import { useLanguage } from '../../context/LanguageContext';
import speechRecognitionService from '../../services/speechRecognition';
import './RecordingModal.css';

const DEFAULT_MAX_SECONDS = 30;
const RELIABILITY_STORAGE_KEY = 'voice_reliability_mode';

const normalizeSegment = (s) => (s || '')
  .toLowerCase()
  .replace(/[.,!?;:()[\]{}"']/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const tokenize = (s) => (s || '').trim().split(/\s+/).filter(Boolean);

const findWordOverlap = (base, incoming, max = 20) => {
  const a = tokenize(base).map((w) => normalizeSegment(w));
  const b = tokenize(incoming).map((w) => normalizeSegment(w));
  const maxLen = Math.min(max, a.length, b.length);
  for (let len = maxLen; len >= 1; len--) {
    const suffix = a.slice(-len).join(' ');
    const prefix = b.slice(0, len).join(' ');
    if (suffix && suffix === prefix) return len;
  }
  return 0;
};

const mergeTranscript = (base, incoming) => {
  const b = (base || '').trim();
  const i = (incoming || '').trim();
  if (!i) return b;
  if (!b) return i;

  const nb = normalizeSegment(b);
  const ni = normalizeSegment(i);

  if (nb === ni) return b;
  if (ni.startsWith(nb)) return i; // engine ha riscritto testo completo con più parole
  if (nb.startsWith(ni) || nb.includes(ni)) return b;
  if (ni.includes(nb)) return i;

  const overlap = findWordOverlap(b, i, 20);
  if (overlap > 0) {
    const tail = tokenize(i).slice(overlap).join(' ');
    return tail ? `${b} ${tail}`.trim() : b;
  }

  return `${b} ${i}`.trim();
};

const shouldSkipChunk = (prevNorm, nextNorm, mode = 'normal') => {
  if (!nextNorm) return true;
  if (!prevNorm) return false;
  if (prevNorm === nextNorm) return true;
  if (prevNorm.includes(nextNorm) || nextNorm.includes(prevNorm)) return true;

  const a = new Set(prevNorm.split(/\s+/));
  const b = nextNorm.split(/\s+/);
  const overlap = b.filter((w) => a.has(w)).length;
  const threshold = mode === 'safe' ? 0.7 : mode === 'guarded' ? 0.8 : 0.85;
  return b.length > 0 && overlap / b.length >= threshold;
};

/**
 * Modal di registrazione vocale: l'utente preme Record, parla, poi Stop.
 * Trascrizione in chunk (salvati a ogni onEnd del browser), poi ricomposizione
 * senza AI (overlap + dedupe). Il testo finale viene passato a onDone e il modal si chiude.
 */
const RecordingModal = ({
  isOpen,
  onClose,
  onDone,
  maxRecordingSeconds = DEFAULT_MAX_SECONDS,
}) => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(maxRecordingSeconds);
  const committedTranscriptRef = useRef('');
  const currentInterimRef = useRef('');
  const stopRequestedRef = useRef(false);
  const isRecordingRef = useRef(false);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const reliabilityModeRef = useRef('normal'); // normal | guarded | safe
  const metricsRef = useRef({
    rapidEnds: 0,
    errors: 0,
    duplicateSkips: 0,
    lastEndAt: 0,
    sessionStartAt: 0,
  });

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      committedTranscriptRef.current = '';
      currentInterimRef.current = '';
      stopRequestedRef.current = false;
      isRecordingRef.current = false;
      setIsStarting(false);
      setErrorMessage('');
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      metricsRef.current = {
        rapidEnds: 0,
        errors: 0,
        duplicateSkips: 0,
        lastEndAt: 0,
        sessionStartAt: 0,
      };

      try {
        const savedMode = sessionStorage.getItem(RELIABILITY_STORAGE_KEY);
        if (savedMode === 'normal' || savedMode === 'guarded' || savedMode === 'safe') {
          reliabilityModeRef.current = savedMode;
        } else {
          reliabilityModeRef.current = 'normal';
        }
      } catch {
        reliabilityModeRef.current = 'normal';
      }
    }
  }, [isOpen]);

  const setReliabilityMode = (nextMode) => {
    if (!nextMode || reliabilityModeRef.current === nextMode) return;
    reliabilityModeRef.current = nextMode;
    try {
      sessionStorage.setItem(RELIABILITY_STORAGE_KEY, nextMode);
    } catch {
      // ignore storage failures
    }
  };

  const evaluateReliabilityMode = () => {
    const m = metricsRef.current;
    // Pattern tipico device instabile: molti onEnd ravvicinati + errori + dedup frequente
    if (m.errors >= 2 || m.rapidEnds >= 8 || m.duplicateSkips >= 5) {
      setReliabilityMode('safe');
      return;
    }
    if (m.errors >= 1 || m.rapidEnds >= 4 || m.duplicateSkips >= 3) {
      setReliabilityMode('guarded');
      return;
    }
    setReliabilityMode('normal');
  };

  const handleClose = () => {
    if (isRecording) {
      stopRequestedRef.current = true;
      speechRecognitionService.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsRecording(false);
    setIsTranscribing(false);
    setIsStarting(false);
    setErrorMessage('');
    onClose();
  };

  const finishAndRecompose = () => {
    let text = (committedTranscriptRef.current || '').trim();
    const interim = (currentInterimRef.current || '').trim();
    if (interim) {
      text = mergeTranscript(text, interim);
    }
    setIsTranscribing(false);
    if (text && onDone) onDone(text);
    onClose();
  };

  const startRecording = () => {
    if (!speechRecognitionService.getSupported()) {
      return;
    }
    committedTranscriptRef.current = '';
    currentInterimRef.current = '';
    stopRequestedRef.current = false;
    isRecordingRef.current = true;
    setErrorMessage('');
    setIsRecording(true);
    setTimeRemaining(maxRecordingSeconds);
    startTimeRef.current = Date.now();
    metricsRef.current.sessionStartAt = Date.now();

    const tick = () => {
      if (stopRequestedRef.current || !isRecordingRef.current) return;
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      const remaining = Math.max(0, maxRecordingSeconds - elapsed);
      setTimeRemaining(remaining);
      if (remaining <= 0) {
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = null;
        stopRequestedRef.current = true;
        speechRecognitionService.stop();
      }
    };
    tick();
    timerRef.current = setInterval(tick, 200);

    const pushChunkAndRestart = () => {
      currentInterimRef.current = '';
      if (!stopRequestedRef.current && isRecordingRef.current) {
        const restartDelay = reliabilityModeRef.current === 'safe'
          ? 320
          : reliabilityModeRef.current === 'guarded'
            ? 180
            : 50;
        setTimeout(() => {
          if (!stopRequestedRef.current && isRecordingRef.current) {
            speechRecognitionService.start(onResult, onError, onEnd);
          }
        }, restartDelay);
      }
    };

    const onResult = (final, interim) => {
      const f = (final || '').trim();
      const i = (interim || '').trim();
      if (f) {
        const before = committedTranscriptRef.current;
        const merged = mergeTranscript(before, f);
        if (normalizeSegment(merged) === normalizeSegment(before)) {
          metricsRef.current.duplicateSkips += 1;
          evaluateReliabilityMode();
        }
        committedTranscriptRef.current = merged;
      }
      currentInterimRef.current = i;
    };

    const onError = (error) => {
      setIsStarting(false);
      metricsRef.current.errors += 1;
      evaluateReliabilityMode();
      if (error === 'no-speech') {
        setErrorMessage(t('freeTextSearch.recordingModalNoSpeech'));
        return;
      }
      if (error === 'not-allowed' || error === 'service-not-allowed') {
        setErrorMessage(t('freeTextSearch.recordingModalMicDenied'));
      } else if (error === 'audio-capture') {
        setErrorMessage(t('freeTextSearch.recordingModalMicUnavailable'));
      } else if (error === 'network') {
        setErrorMessage(t('freeTextSearch.recordingModalNetworkError'));
      } else {
        setErrorMessage(t('freeTextSearch.voiceError'));
      }
      setIsRecording(false);
      isRecordingRef.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };

    const onEnd = () => {
      const now = Date.now();
      if (metricsRef.current.lastEndAt && now - metricsRef.current.lastEndAt < 2500) {
        metricsRef.current.rapidEnds += 1;
        evaluateReliabilityMode();
      }
      metricsRef.current.lastEndAt = now;

      const pastMaxTime = startTimeRef.current && (Date.now() - startTimeRef.current) / 1000 >= maxRecordingSeconds;
      if (stopRequestedRef.current || pastMaxTime) {
        if (pastMaxTime) {
          stopRequestedRef.current = true;
          isRecordingRef.current = false;
        }
        setIsRecording(false);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        setIsTranscribing(true);
        finishAndRecompose();
        return;
      }
      pushChunkAndRestart();
    };

    setIsStarting(true);
    const onResultWithStarted = (final, interim) => {
      setIsStarting((prev) => (prev ? false : prev));
      onResult(final, interim);
    };
    const started = speechRecognitionService.start(onResultWithStarted, (err) => {
      onError(err);
      setIsStarting(false);
    }, onEnd);
    if (!started) setIsStarting(false);
  };

  const stopRecording = () => {
    stopRequestedRef.current = true;
    isRecordingRef.current = false;
    speechRecognitionService.stop();
  };

  if (!isOpen) return null;

  return (
    <div className="recording-modal-overlay" onClick={handleClose}>
      <div className="recording-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="recording-modal-actions">
          <button
            type="button"
            className={`recording-modal-image-btn ${isRecording ? 'is-recording' : ''}`}
            onClick={isRecording ? stopRecording : startRecording}
            aria-label={isRecording ? t('freeTextSearch.recordingModalStop') : t('freeTextSearch.recordingModalRecord')}
          >
            <img
              src={`${process.env.PUBLIC_URL}/img/record.jpg`}
              alt="Record"
              className="recording-modal-image"
            />
          </button>

          {isRecording && (
            <p className="recording-modal-helper">
              {t('freeTextSearch.recordingModalTapToStop')}
            </p>
          )}

          {!isRecording && !isTranscribing && !isStarting && (
            <p className="recording-modal-helper">
              {t('freeTextSearch.recordingModalTapToRecord')}
            </p>
          )}

          {isRecording && (
            <p className="recording-modal-timer">
              {t('freeTextSearch.recordingModalTimeLeft').replace('{seconds}', timeRemaining)}
            </p>
          )}

          {isTranscribing && (
            <p className="recording-modal-status">
              {t('freeTextSearch.recordingModalTranscribing')}
            </p>
          )}
          {!!errorMessage && (
            <p className="recording-modal-status recording-modal-status--error">
              {errorMessage}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecordingModal;
